import React from 'react'
import { ReactComponent as SuccessIcon } from '../../images/icons/check_fill.svg'
import { MechanicalCounter } from 'mechanical-counter'
import { formatNumber } from '../../lib/utils/formatNumber'
import AudienceSuccessItem from '../../components/Audiences/AudienceSuccessItem'
import { isEmpty } from 'lodash'
import { Button } from '@demandlocal/components'
import { useNavigate } from 'react-router'
import { useDealership } from '../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'

type Props = {
  audienceDetails: {
    audience_count?: number
    formValues?: {
      audience_name: string
      description: string
      customer_source_id: Array<{ label: string }>
      vehicle_source: Array<{
        Make: { label: string }
        Model: { label: string }
        Year: { label: string }
      }>
      customer_type: Array<{ label: string }>
    }
  }
  onUploadAudiencePressed: () => void
}

const AudienceSuccess = ({
  audienceDetails,
  onUploadAudiencePressed,
}: Props): JSX.Element => {
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full font-Spartan'}>
      <div className={'flex flex-1 flex-col overflow-auto'}>
        <div className={'flex flex-col pt-12 justify-center items-center'}>
          <SuccessIcon />
          <div className={'pt-5 font-bold text-[28px]'}>
            Great! Your audience is created
          </div>
          <div className={'pt-3 font-medium text-base'}>
            You can now deploy it to a campaign
          </div>
        </div>
        <div className={'pt-20 flex flex-row justify-between items-center'}>
          <span className={'font-bold text-base text-primary-xiketic'}>
            Who are you targeting
          </span>
          <div className={'flex flex-row items-center'}>
            <span className={'font-medium mr-3 text-base text-primary-xiketic'}>
              Audience size
            </span>
            <div className={'p-3 font-bold bg-secondary-sunglow/20'}>
              <MechanicalCounter text={formatNumber(audienceDetails.audience_count)} />
            </div>
          </div>
        </div>
        <div className={'pt-20 flex justify-between items-center border-b-2 pb-5'}>
          <div className={'font-bold text-sm'}>Audience name</div>
          <div className={'px-4 py-2 bg-gray-100 font-medium text-xs'}>
            {audienceDetails.formValues?.audience_name}
          </div>
        </div>
        <div className={'pt-10 flex justify-between items-center border-b-2 pb-5'}>
          <div className={'font-bold text-sm'}>Description</div>
          <div className={'px-4 py-2 bg-gray-100 font-medium text-xs'}>
            {audienceDetails.formValues?.description}
          </div>
        </div>
        {!isEmpty(audienceDetails.formValues?.customer_source_id) && (
          <AudienceSuccessItem
            name={'Customer source'}
            values={
              audienceDetails.formValues?.customer_source_id.map(
                (item: { label: string }) => item.label,
              ) ?? []
            }
          />
        )}
        {!isEmpty(audienceDetails.formValues?.vehicle_source) && (
          <AudienceSuccessItem
            name={'Vehicles'}
            values={
              audienceDetails.formValues?.vehicle_source.map(
                (item: {
                  Make: { label: string }
                  Model: { label: string }
                  Year: { label: string }
                }) => {
                  return `${item?.Make?.label} ${item?.Model?.label ?? ''} ${
                    item?.Year?.label ?? ''
                  }`
                },
              ) ?? []
            }
          />
        )}
        {!isEmpty(audienceDetails.formValues?.customer_type) && (
          <AudienceSuccessItem
            name={'Customer type'}
            values={
              audienceDetails.formValues?.customer_type.map(
                (item: { label: string }) => item.label,
              ) ?? []
            }
          />
        )}
      </div>
      <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            navigate(t('dealership.audiences.custom', { dealership }))
          }}>
          Close
        </Button>
        <div className={'flex-row flex'}>
          <div className={'pr-4'}>
            <Button
              variant={'dropdown-option'}
              onClick={(e) => {
                e.preventDefault()
                navigate(t('dealership.audiences.custom', { dealership }))
              }}>
              Deploy later
            </Button>
          </div>
          <Button variant={'primary'} onClick={onUploadAudiencePressed} type={'button'}>
            Deploy now
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AudienceSuccess
